/**
 * 语法知识点分类（Knowledge Points）
 * ------------------------------------------------------------
 * 为什么需要这张表：
 *   教材里的语法点写法五花八门（"There be 句型"、"there is/are"、"There is a ..."），
 *   如果直接拿原文分类当筛选条件，同一个知识点会被拆成好几类，孩子点进去只能做一两道题。
 *
 * 做法：
 *   1) 定义一组**标准知识点**（下面 GRAMMAR_POINTS），每个带 id、名称、关键词
 *   2) 把教材里的原始分类 / 规则文本按关键词归一到标准知识点
 *   3) 无法归一的，保留原文分类单独成类（不丢弃内容，也不硬塞）
 *
 * 注意：这张表只做"归类"，不生成任何题目内容；
 *      教材没写的语法点不会凭空出现。
 */

export const GRAMMAR_POINTS = [
  {
    id: 'be',
    name: 'be 动词',
    icon: '🅰️',
    desc: 'am / is / are 的用法与选择',
    keywords: ['be 动词', 'be动词', 'am/is/are', 'am is are', 'is/are', 'was/were', '形容词前用 be'],
  },
  {
    id: 'have',
    name: 'have / has',
    icon: '🤲',
    desc: '表示"有"，注意第三人称单数用 has',
    keywords: ['have / has', 'have/has', 'have has', '表示"有"', 'have 表示', 'has 表示'],
  },
  {
    id: 'pronoun',
    name: '人称代词',
    icon: '🙋',
    desc: 'I / you / he / she / it / we / they 及宾格',
    keywords: ['人称代词', '主格', '宾格', 'pronoun', 'he she it'],
  },
  {
    id: 'possessive',
    name: '物主代词',
    icon: '🎒',
    desc: 'my / your / his / her 与 mine / yours / his / hers',
    keywords: ['物主代词', '所有格', 'possessive', '名词所有格', 'mine', 'my / your', 'his / her'],
  },
  {
    id: 'can',
    name: 'can + 动词原形',
    icon: '💪',
    desc: 'can 后面永远跟动词原形，没有人称变化',
    keywords: ['can + 动词原形', 'can 表达能力', 'can not', '情态动词', '表达能力', 'could'],
  },
  {
    id: 'there-be',
    name: 'there is / there are',
    icon: '📍',
    desc: '某处有某物；单数 is，复数 are',
    keywords: ['there be', 'there is', 'there are', 'there is / there are'],
  },
  {
    id: 'third-person',
    name: '一般现在时第三人称单数',
    icon: '⏱️',
    desc: 'he / she / it 作主语时动词加 -s / -es',
    keywords: ['第三人称单数', '三单', '一般现在时', '动词三单', '动词第三人称'],
  },
  {
    id: 'frequency',
    name: '频度副词',
    icon: '🔁',
    desc: 'always / often / usually / sometimes / never 的位置',
    keywords: ['频度副词', 'often', 'always', 'usually', 'sometimes'],
  },
  {
    id: 'plural',
    name: '名词单复数',
    icon: '🔢',
    desc: '可数名词的复数变化',
    keywords: ['名词单复数', '单复数', '复数变化', 'plural'],
  },
  {
    id: 'verb-phrase',
    name: '动词短语与搭配',
    icon: '🧩',
    desc: 'look after / take a walk / go shopping 等固定搭配',
    keywords: ['动词短语', '固定搭配', '短语', 'look after', 'go + doing'],
  },
  {
    id: 'phonics',
    name: '语音（字母组合）',
    icon: '🔤',
    desc: 'ch / sh / ck / wh 等字母组合的发音',
    keywords: ['字母组合', '发音', 'phonics', '语音'],
  },
];

/** id → 知识点 */
export const POINT_BY_ID = new Map(GRAMMAR_POINTS.map(p => [p.id, p]));

/**
 * 把一段中文分类/规则文本归一到标准知识点 id。
 * 命中多个时取"关键词最长"的那个，避免 "have" 抢走 "have / has"。
 * @returns {string|null}
 */
export function classifyGrammar(text) {
  const s = String(text || '').toLowerCase();
  if (!s) return null;

  // 语音类优先判定：分类名里出现「字母组合 / 发音 / phonics」一律归语音，
  // 否则 "字母组合 wh 的发音" 会因为例子里有 whose 被误判成物主代词。
  if (/字母组合|发音|phonics|拼读/.test(s)) return 'phonics';

  let best = null;
  for (const p of GRAMMAR_POINTS) {
    for (const kw of p.keywords) {
      if (s.includes(String(kw).toLowerCase())) {
        if (!best || kw.length > best.len) best = { id: p.id, len: kw.length };
      }
    }
  }
  return best?.id || null;
}

/**
 * 把一个单元里的语法点 + 语法题聚合成"知识点分组"。
 * @param {object} content getUnitContent 的返回值
 * @returns {Array<{id,name,icon,desc,questions:Array,points:Array}>}
 *          顺序：先按 GRAMMAR_POINTS 的定义顺序，再加"其他"分类
 */
export function groupGrammarByPoint(content) {
  const groups = new Map();
  const ensure = (id, fallbackName) => {
    if (!groups.has(id)) {
      const meta = POINT_BY_ID.get(id);
      groups.set(id, {
        id,
        name: meta?.name || fallbackName || id,
        icon: meta?.icon || '🧩',
        desc: meta?.desc || '',
        points: [],
        questions: [],
      });
    }
    return groups.get(id);
  };

  // 教师/教材写的语法点
  for (const g of content.grammar || []) {
    const id = classifyGrammar(`${g.category} ${g.rule}`);
    if (!id) {
      // 无法归一的：用原文分类单独成组（不硬塞）
      const key = `raw:${g.category}`;
      const grp = ensure(key, g.category);
      grp.points.push(g);
      continue;
    }
    ensure(id).points.push(g);
  }

  // 语法题（按题目自带的 grammarCategory 归类）
  for (const q of content.questions || []) {
    if (q.skill !== 'G') continue;
    const id = classifyGrammar(q.grammarCategory || q.prompt);
    if (!id) {
      const key = `raw:${q.grammarCategory || '未分类'}`;
      ensure(key, q.grammarCategory || '未分类').questions.push(q);
      continue;
    }
    ensure(id).questions.push(q);
  }

  // 排序：标准知识点按定义顺序，raw: 的放最后
  const order = id => {
    if (id.startsWith('raw:')) return 1000;
    const i = GRAMMAR_POINTS.findIndex(p => p.id === id);
    return i < 0 ? 999 : i;
  };
  return [...groups.values()]
    .filter(g => g.points.length || g.questions.length)
    .sort((a, b) => order(a.id) - order(b.id));
}

/** 兼容旧接口：保留一份扁平分类列表（用于"混合练习"等） */
export function grammarCategoriesOf(content) {
  return groupGrammarByPoint(content).map(g => g.name);
}
